import React, { useState } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Page } from '../../../../layout';
import PageInstructions from '@shared/components/PageInstructions';
import { Button, FormGroup, FormControlLabel } from '@mui/material';
import MuiSwitch from '@shared/components/MuiSwitch';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { checkPointData } from '../../../checkPointsCache';
import ErrorMessage from '@shared/components/ErrorMessage';
import { PERFORMANCE_METRIC_STATUSES } from '@shared/constants/checkpoint-performance-report-metric-enums';
import PerformanceMetricPageAddControls from '../PerformanceMetricPageAddControls';
import ApolloError from '@shared/components/ApolloError';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '@shared/services/pageTitleService';
import GET_ORG_UNIT_INFO from '@shared/queries/getOrgUnitInfoQuery';
import {
  getMetricProperties,
  getPerformanceReportMetricFieldsDefaultErrors
} from '../pageSettings/pageSettingsByPageMode';
import LinkButton from '../../../../shared/components/LinkButton';
import { newLine } from '../../../../../constants/textConstants';
import { userTier } from '@apollo-internal/cache';
import { canUserAccessSeveralOrganization } from '@shared/services/userAccessibilityService';
import OrgUnitBreadcrumbAnnotation from '../../../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';
import { PAGE_VIEW_MODES } from '../../../../../constants/page-view-modes';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import { PerformanceMetricService } from '../services/performanceMetricService';
import { PerformanceMetricValidator } from '../../validators/PerformanceMetricValidator';

/**
 * @typedef {object} AddPerformanceMetricProps
 * @property {*} [pageMode]
 */

/**
 * @param {AddPerformanceMetricProps} props
 */
const AddPerformanceMetric = props => {
  const { pageMode = PAGE_VIEW_MODES.ADD } = props;

  const { orgUnit, appraisal, checkPointId } = useParams();
  const orgUnitId = parseInt(orgUnit);
  const appraisalId = parseInt(appraisal);
  const selectedCheckPointId = parseInt(checkPointId);

  const checkPointCache = useReactiveVar(checkPointData);
  const tier = useReactiveVar(userTier);

  const pageSettings = getPageSettings();
  const previousLink = `/check-points/${orgUnitId}/${pageMode}-check-point/${appraisalId}/${selectedCheckPointId}`;

  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [showDescriptions, setShowDescriptions] = useState(true);
  const [validationErrors, setValidationErrors] = useState({
    ...pageSettings.performanceReportMetricFieldsErrorObject()
  });

  const [formData, setFormData] = useState({ ...pageSettings.performanceMetricFields() });
  const [selectedMetricProperty, setSelectedMetricProperty] = useState(null);

  const navigate = useNavigate();

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([orgUnitName, 'Add Performance Report Metric']);
  const commonItems = [renderedOrgUnit, `Appraisal ${appraisalId}`];
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, ...commonItems]
    : commonItems;

  const breadcrumbPathItems = [
    { name: 'Check Points', url: `/check-points/${orgUnitId}` },
    {
      name: pageMode === PAGE_VIEW_MODES.EDIT ? 'Edit Check Point' : 'Add Check Point',
      url: previousLink
    },
    { name: 'Add Performance Report Metric', url: '' }
  ];

  const handleClickOutside = () => {
    setSelectedMetricProperty(null);
  };

  const handleOnClickMetricField = clickedField => {
    const metricProperty = pageSettings.metricProperties.find(x => x.field === clickedField);
    setSelectedMetricProperty(metricProperty);
  };

  const handleFieldChange = (serviceMethod, param) => {
    const { newFormData, newValidationErrors } = serviceMethod(
      param,
      formData,
      validationErrors,
      getDefaultErrorObject
    );
    setFormData(newFormData);
    setValidationErrors(newValidationErrors);
  };

  const handleOnChange = param => {
    updateValidationErrors(param);
    setFormData({ ...formData, ...param });
  };

  const handleOnChangeMeasurementCalculation = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeMeasurementCalculation, param);
  };

  const handleOnChangeTimeBounds = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeTimeBounds, param);
  };

  const handleOnChangeQualityDomain = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeQualityDomain, param);
  };

  const handleOnChangeLowerUpperLimit = param => {
    const { newFormData, newValidationErrors } =
      PerformanceMetricService.handleOnChangeLowerUpperLimit(
        param,
        formData,
        validationErrors,
        getNewErrorObject
      );
    setFormData(newFormData);
    setValidationErrors(newValidationErrors);
  };

  const handleOnChangeActual = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeActual, param);
  };

  const isFormValid = () => {
    const fields = Object.keys(formData).filter(key => !['tempId', 'id', 'key'].includes(key));
    const errors = PerformanceMetricValidator.validatePerformanceMetric(
      formData,
      validationErrors,
      fields,
      pageSettings,
      getNewErrorObject,
      getDefaultErrorObject
    );

    setValidationErrors(errors);
    setShowValidationMessage(true);
    return !hasErrors(errors);
  };

  const updateValidationErrors = param => {
    const value = Object.values(param)[0];
    const key = Object.keys(param)[0];

    setValidationErrors({
      ...validationErrors,
      [`${key}`]: getNewErrorObject(validationErrors[`${key}`], { hasError: !value })
    });
  };

  const hasErrors = errors => Object.values(errors).filter(x => x.hasError).length > 0;

  const getDefaultErrorObject = prevError => {
    return { ...prevError, ...{ hasError: false, errorMessage: '' } };
  };

  const getNewErrorObject = (prevError, error) => {
    return { ...prevError, ...error };
  };

  const redirectToCheckPointsPage = () => {
    navigate(previousLink);
  };

  const handleCancel = () => {
    setFormData({ ...pageSettings.performanceMetricFields() });
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      updatePerformanceReportMetric();
      redirectToCheckPointsPage();
    }
  };

  const updatePerformanceReportMetric = () => {
    const cachedPerformanceMetrics = checkPointCache.performanceMetrics;
    const metric = {
      ...formData,
      tempId: cachedPerformanceMetrics.length,
      referenceCheckpointDatapoint: null,
      timeFrame: formData.timeFrame || null,
      lowerLimit: formData.lowerLimit ? parseFloat(formData.lowerLimit) : null,
      upperLimit: formData.upperLimit ? parseFloat(formData.upperLimit) : null,
      actual: parseFloat(formData.actual),
      addedInCurrentCheckpoint: true,
      isDeletable: true
    };

    cachedPerformanceMetrics.push(metric);
    checkPointData({ ...checkPointCache, performanceMetrics: cachedPerformanceMetrics });
  };

  if (orgUnitData && orgUnitData.mddapOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (orgUnitError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='performance-report-metric-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}
      />

      <div className='performance-report-metric-page__container' onClick={handleClickOutside}>
        <PerformanceMetricPageAddControls
          handleOnChangeLowerUpperLimit={handleOnChangeLowerUpperLimit}
          handleOnChangeMeasurementCalculation={handleOnChangeMeasurementCalculation}
          handleOnChangeTimeBounds={handleOnChangeTimeBounds}
          handleOnChangeActual={handleOnChangeActual}
          handleOnChangeQualityDomain={handleOnChangeQualityDomain}
          handleOnChange={handleOnChange}
          handleOnClickMetricField={handleOnClickMetricField}
          selectedMetricProperty={selectedMetricProperty}
          showDescriptions={showDescriptions}
          validationErrors={validationErrors}
          pageSettings={pageSettings}
          performanceMetric={formData}
        />

        <div className='sticky-section'>
          <div className='instructions'>
            <div className='action-button-group'>
              <div className='action-buttons'>
                <Button
                  type='submit'
                  data-testid='submit-button'
                  className='primary-button'
                  onClick={handleSubmit}>
                  Submit
                </Button>
                <LinkButton
                  data-testid='cancel-button'
                  className='secondary-button'
                  type='button'
                  to={previousLink}
                  onClick={handleCancel}>
                  Cancel
                </LinkButton>
              </div>
              {showValidationMessage && hasErrors(validationErrors) && (
                <ErrorMessage
                  message='Metric errors must be resolved before submission.'
                  className='button-group-error-message'
                />
              )}
            </div>
            <PageInstructions
              title='Add Performance Report Metric'
              description={pageSettings.description}
            />
          </div>
          <FormGroup className='switch-container'>
            <FormControlLabel
              key='FieldDescriptions'
              labelPlacement='start'
              label='Field Descriptions'
              className='switch-label'
              data-testid='FieldDescriptions'
              control={
                <MuiSwitch
                  checked={showDescriptions}
                  onChange={(_, checked) => setShowDescriptions(checked)}
                />
              }
            />
          </FormGroup>
        </div>
      </div>
    </Page>
  );
};

const getPageSettings = () => {
  return {
    performanceMetricFields: _ => ({
      measurementName: '',
      measurementDescription: '',
      measurementCalculation: null,
      measurementUnit: '',
      numerator: '',
      denominator: '',
      targetDescription: '',
      goodPerformanceIndicator: null,
      timeBounds: null,
      timeFrame: null,
      captureFrequency: null,
      reportFrequency: null,
      measurementLevel: null,
      measurementUse: '',
      limitationsOrBlindSpots: '',
      qualityDomain: null,
      customQualityDomainDefinition: '',
      businessObjective: '',
      qualityObjective: '',
      lowerLimit: '',
      upperLimit: '',
      actual: '',
      notes: '',
      status: PERFORMANCE_METRIC_STATUSES.Update.Id
    }),
    description: (
      <>
        Carefully read the description for each field and enter all required* fields and
        conditionally** required fields. Fields are dropdown selections, numerical entry, or open
        text. The majority of these metric fields may only be entered once and cannot be edited
        later. Click “Submit” to add the new metric, or “Cancel” to go back to the Add Check Point
        page. Field descriptions show by default, but can be hidden to streamline the page view. For
        additional information, review the{' '}
        <a href='https://cmmiinstitute.com/resource-files/public/pcs/mddap/participants-only/mddap-check-point-guidelines'>
          MDDAP Check Point Guidelines
        </a>
        .
      </>
    ),
    metricProperties: getMetricProperties([
      {
        field: 'measurementCalculation',
        description:
          'Select an option from the dropdown which best describes the metric calculation. ' +
          newLine +
          ' - Rate: comparison of two numbers with different units.' +
          newLine +
          ' - Ratio: comparison of two numbers with the same unit.' +
          newLine +
          newLine +
          'If using an average, indicate this in the “unit of measure,” “numerator,” or “denominator” field.'
      },
      {
        field: 'numerator',
        addDoubleTitleAsterisk: true
      },
      {
        field: 'denominator',
        addDoubleTitleAsterisk: true
      },
      {
        field: 'targetDescription',
        description: 'Describe the target objective or goal for the metric.'
      },
      {
        field: 'goodPerformanceIndicator',
        description:
          'Select an option from the dropdown which best describes ' +
          'the desired long-term trend for this metric.'
      },
      {
        field: 'timeBounds',
        description:
          'Select an option from the dropdown which best describes the data that the metric represents.' +
          newLine +
          ' - All Time: # represents entire period since began measuring (best for cumulative metrics)' +
          newLine +
          ' - Period of Time: # represents a static time (best for metrics assigned to specific month,' +
          ' quarter, etc.)' +
          newLine +
          ' - Rolling Time: # represents an ongoing time (best for metrics assigned to the most recent # ' +
          'of days, months, etc.)'
      },
      {
        field: 'timeFrame',
        description:
          'Select an option from the dropdown which best describes the time that the metric represents.' +
          ' This may be the same as how often the metric is captured or reported.',
        addDoubleTitleAsterisk: true
      },
      {
        field: 'measurementLevel',
        description:
          'Select an option from the dropdown which best describes ' +
          'which products are represented by the metric.'
      },
      {
        field: 'qualityDomain',
        description:
          'Select an option from the dropdown which best describes the metric, ' +
          'or select N/A if none apply.'
      },
      {
        field: 'customQualityDomainDefinition',
        description:
          'Describe how this metric provides insight into product quality ' +
          'or organizational performance.',
        addDoubleTitleAsterisk: true
      },
      {
        field: 'businessObjective',
        description:
          'Describe the business objective most closely associated with the quality domain selected.'
      },
      {
        field: 'qualityObjective',
        description:
          'Describe the quality objective most closely associated with the quality domain selected.'
      }
    ]),
    performanceReportMetricFieldsErrorObject: getPerformanceReportMetricFieldsDefaultErrors
  };
};

export default AddPerformanceMetric;
